import { useMemo } from 'react';

import { Form } from 'antd';

import {
  DOWNLOAD_FOLDER_PRODUCT_HIERARCHY,
  DOWNLOAD_FOLDER_PRODUCT_CONFIGS,
} from 'common/components/modal-download-folder/constants';

export const useDownloadProductConfigs = () => {
  const form = Form.useFormInstance();
  const productConfigs = {
    //* UNIT
    productUnitPlanogram: Form.useWatch('productUnitPlanogram', form),
    productUnitMarketing: Form.useWatch('productUnitMarketing', form),
    productUnitAdditionInformation: Form.useWatch(
      'productUnitAdditionInformation',
      form
    ),
    //* INNER PACK
    productInnerPackPlanogram: Form.useWatch('productInnerPackPlanogram', form),
    productInnerPackMarketing: Form.useWatch('productInnerPackMarketing', form),
    productInnerPackAdditionInformation: Form.useWatch(
      'productInnerPackAdditionInformation',
      form
    ),
    //* CASE
    productCasePlanogram: Form.useWatch('productCasePlanogram', form),
    productCaseMarketing: Form.useWatch('productCaseMarketing', form),
    productCaseAdditionInformation: Form.useWatch(
      'productCaseAdditionInformation',
      form
    ),
    //* MASTER CASE
    productMasterCasePlanogram: Form.useWatch(
      'productMasterCasePlanogram',
      form
    ),
    productMasterCaseMarketing: Form.useWatch(
      'productMasterCaseMarketing',
      form
    ),
    productMasterCaseAdditionInformation: Form.useWatch(
      'productMasterCaseAdditionInformation',
      form
    ),
    //* PALLET
    productPalletPlanogram: Form.useWatch('productPalletPlanogram', form),
    productPalletMarketing: Form.useWatch('productPalletMarketing', form),
    productPalletAdditionInformation: Form.useWatch(
      'productPalletAdditionInformation',
      form
    ),
    //* DISPLAY SHIPPER
    productDisplayShipperPlanogram: Form.useWatch(
      'productDisplayShipperPlanogram',
      form
    ),
    productDisplayShipperMarketing: Form.useWatch(
      'productDisplayShipperMarketing',
      form
    ),
    productDisplayShipperAdditionInformation: Form.useWatch(
      'productDisplayShipperAdditionInformation',
      form
    ),
  };

  const getAllImageTypeStatus = () => {
    return Object.keys(DOWNLOAD_FOLDER_PRODUCT_CONFIGS).reduce(
      (accumulator, imageType) => {
        const configValue = productConfigs[imageType];
        const maxOptValue = DOWNLOAD_FOLDER_PRODUCT_CONFIGS[imageType];

        if (!configValue?.length) accumulator[imageType] = 'unchecked';
        else if (configValue?.length === maxOptValue.length)
          accumulator[imageType] = 'checked';
        else if (configValue?.length < maxOptValue.length)
          accumulator[imageType] = 'indeterminate';

        return accumulator;
      },
      {}
    );
  };

  const getAllHierarchyStatus = (imageTypesStatus) => {
    return Object.keys(DOWNLOAD_FOLDER_PRODUCT_HIERARCHY).reduce(
      (accumulator, hierarchy) => {
        const imageTypes = DOWNLOAD_FOLDER_PRODUCT_HIERARCHY[hierarchy];

        const statusPoint = scoreHierarchyStatus(imageTypes, imageTypesStatus);

        const checkedPoint = imageTypes.length * 2; //* 2 pointer per image type

        //* if all checked
        if (statusPoint === checkedPoint) accumulator[hierarchy] = 'checked';
        //* if all unchecked
        else if (statusPoint === 0) accumulator[hierarchy] = 'unchecked';
        //* else
        else accumulator[hierarchy] = 'indeterminate';

        return accumulator;
      },
      {}
    );
  };

  const scoreHierarchyStatus = (imageTypes, imageTypesStatus) => {
    return imageTypes.reduce((accumulator, imageType) => {
      const imageTypeStatus = imageTypesStatus[imageType];

      if (imageTypeStatus === 'checked') accumulator = accumulator + 2;

      if (imageTypeStatus === 'indeterminate') accumulator = accumulator + 1;

      if (imageTypeStatus === 'unchecked') accumulator = accumulator + 0;

      return accumulator;
    }, 0);
  };

  const productConfigsStatus = useMemo(() => {
    const imageTypesStatus = getAllImageTypeStatus();

    const hierarchyStatus = getAllHierarchyStatus(imageTypesStatus);

    return { ...imageTypesStatus, ...hierarchyStatus };
  }, [JSON.stringify(productConfigs)]);

  const onCheckImageType = (imageType, checked) => {
    if (!imageType) return;

    const updateValues = {
      [imageType]: checked ? DOWNLOAD_FOLDER_PRODUCT_CONFIGS[imageType] : [],
    };

    form.setFieldsValue(updateValues);
  };

  const onCheckHierarchyLevel = (hierarchyLevel, checked) => {
    if (!hierarchyLevel) return;

    const updatedValues = DOWNLOAD_FOLDER_PRODUCT_HIERARCHY[
      hierarchyLevel
    ]?.reduce((accumulator, currentField) => {
      accumulator[currentField] = checked
        ? DOWNLOAD_FOLDER_PRODUCT_CONFIGS[currentField]
        : [];

      return accumulator;
    }, {});

    form.setFieldsValue(updatedValues);
  };

  return {
    productConfigs,
    productConfigsStatus,
    onCheckHierarchyLevel,
    onCheckImageType,
  };
};
